import React, { ReactNode, useContext, useMemo } from 'react';
import type { Poll } from 'schema/poll';
import { usePollContextValue } from '../reducer/machine/utils';
import { PollContext } from '../store/withContext';
import { isAuthQuestion } from '../utils/guards';

type AuthContextReturn = {
  authSkip: () => void;
  authUser: () => void;
  authContinue: () => void;
  refreshAuthUser: () => void;
  showSkip: boolean;
  showAuth: boolean;
  showContinue: boolean;
};

interface AuthContextProps {
  poll: Poll;
  children: ReactNode;
}

const AuthContext = React.createContext<AuthContextReturn | undefined>(undefined);

export const AuthContextProvider = ({ poll, children }: AuthContextProps) => {
  const user = usePollContextValue('user') ?? null;

  const authQuestion = useMemo(() => poll.questions.find(isAuthQuestion), [poll]);

  const { interpreter } = useContext(PollContext);

  const isAuthorized = !!user;

  return (
    <AuthContext.Provider
      value={{
        showAuth: !isAuthorized,
        showContinue: isAuthorized,
        showSkip: !authQuestion?.data.requiresAnswer,
        authSkip: () => {
          interpreter.send({ type: 'skipAuthentication' });
        },
        authContinue: () => {
          interpreter.send({ type: 'continueAuthenticated' });
        },
        authUser: () => {
          interpreter.send({ type: 'startAuthentication' });
        },
        refreshAuthUser: () => {
          interpreter.send({ type: 'refreshAuthentication' });
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthContext is required!');
  return value;
}
